import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { formatApiError } from '../../api';
import PageHeader from '../../components/PageHeader';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, FileSignature, CalendarClock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import SortHeader from '../../components/SortHeader';
import BulkDeleteDialog from '../../components/BulkDeleteDialog';
import { useTableTools, useSortedItems } from '../../components/tableTools';

const CONTRACT_STATUS = {
    draft:           { label: 'Draft',           tone: 'bg-slate-100 text-slate-700 border-slate-200' },
    active:          { label: 'Active',          tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    due_for_renewal: { label: 'Due for Renewal', tone: 'bg-amber-50 text-amber-800 border-amber-200' },
    renewed:         { label: 'Renewed',         tone: 'bg-sky-50 text-sky-700 border-sky-200' },
    cancelled:       { label: 'Cancelled',       tone: 'bg-red-50 text-red-700 border-red-200' },
    expired:         { label: 'Expired',         tone: 'bg-slate-100 text-slate-600 border-slate-200' },
};
const STATUS_ORDER = ['draft', 'active', 'due_for_renewal', 'renewed', 'cancelled', 'expired'];
const GBP = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 }).format(Number(v || 0));

function daysFromToday(iso) {
    if (!iso) return null;
    try { return Math.ceil((new Date(iso) - new Date()) / 86400000); } catch { return null; }
}

const SORT_ACCESSORS = {
    contract_number: c => c.contract_number || '',
    client_name:     c => (c.client_name || '').toLowerCase(),
    status:          c => c.status || '',
    mrr:             c => Number(c.monthly_saas_fee || 0),
    year_one:        c => Number(c.total_inc_vat_year_one || 0),
    renewal_date:    c => c.renewal_date || '',
};

export default function ContractsList() {
    const navigate = useNavigate();
    const [sp] = useSearchParams();
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState(sp.get('status') || 'all');
    const [renewalWindow, setRenewalWindow] = useState(sp.get('renewal_window_days') || 'any');
    const { selected, toggleOne, toggleAll, clear, sortKey, sortDir, changeSort } = useTableTools({ defaultSortKey: 'renewal_date' });
    const [confirmDelete, setConfirmDelete] = useState(null);
    const sortedItems = useSortedItems(items, sortKey, sortDir, SORT_ACCESSORS);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (search) params.search = search;
            if (statusFilter !== 'all') params.status = statusFilter;
            if (renewalWindow !== 'any') params.renewal_window_days = renewalWindow;
            const r = await api.get('/contracts', { params });
            setItems(r.data.items);
            clear();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
        finally { setLoading(false); }
    }, [search, statusFilter, renewalWindow, clear]);

    useEffect(() => { load(); }, [load]);

    const openDeleteDialog = async () => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        try {
            const r = await api.post('/contracts/bulk-delete-preview', { ids });
            setConfirmDelete({ counts: r.data.counts, names: r.data.sample_names, ids });
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed'); }
    };
    const doDelete = async (ids) => {
        try {
            const r = await api.post('/contracts/bulk-delete', { ids });
            const d = r.data.deleted || {};
            toast.success(`Deleted ${d.contracts || 0} contract(s) + ${(d.invoices || 0) + (d.payments || 0)} linked records`);
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Delete failed'); }
    };

    return (
        <div data-testid="contracts-list-page" className="bm-fade-in">
            <PageHeader
                title="Contracts"
                description="Signed agreements created when clients accept proposals. Track renewals and status here."
                actions={isAdmin && selected.size > 0 ? (
                    <Button onClick={openDeleteDialog} variant="outline" className="h-9 text-red-600 border-red-200 hover:bg-red-50" data-testid="contracts-bulk-delete-btn">
                        <Trash2 size={15} className="mr-1.5" /> Delete {selected.size} selected
                    </Button>
                ) : undefined}
            >
                <div className="mt-5 flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[240px] max-w-sm">
                        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--bm-text-subtle)]" />
                        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by number, client, proposal…" className="pl-9 h-9 bg-white" data-testid="contracts-search-input" />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="h-9 w-[170px] bg-white" data-testid="contracts-status-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            {STATUS_ORDER.map(s => <SelectItem key={s} value={s}>{CONTRACT_STATUS[s].label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={renewalWindow} onValueChange={setRenewalWindow}>
                        <SelectTrigger className="h-9 w-[190px] bg-white" data-testid="contracts-renewal-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="any">Any renewal date</SelectItem>
                            <SelectItem value="7">Renewing in 7 days</SelectItem>
                            <SelectItem value="30">Renewing in 30 days</SelectItem>
                            <SelectItem value="60">Renewing in 60 days</SelectItem>
                            <SelectItem value="90">Renewing in 90 days</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </PageHeader>

            <div className="px-8 py-6">
                <div className="bg-white rounded-xl border border-[color:var(--bm-border)] overflow-hidden">
                    <table className="w-full text-[13px]">
                        <thead className="bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]">
                            <tr>
                                {isAdmin && (
                                    <th className="w-10 px-4 py-3">
                                        <input type="checkbox" checked={items.length > 0 && selected.size === items.length}
                                               onChange={() => toggleAll(items)} className="cursor-pointer"
                                               data-testid="contracts-select-all" />
                                    </th>
                                )}
                                <SortHeader label="Contract"         sortKey="contract_number" currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} />
                                <SortHeader label="Client"           sortKey="client_name"     currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} />
                                <SortHeader label="Status"           sortKey="status"          currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} />
                                <SortHeader label="MRR"              sortKey="mrr"             currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} align="right" />
                                <SortHeader label="Year-one incl. VAT" sortKey="year_one"      currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} align="right" />
                                <SortHeader label="Renewal"          sortKey="renewal_date"    currentSortKey={sortKey} currentSortDir={sortDir} onSort={changeSort} />
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={isAdmin ? 7 : 6} className="px-5 py-10 text-center text-[color:var(--bm-text-muted)]">Loading…</td></tr>}
                            {!loading && sortedItems.length === 0 && (
                                <tr>
                                    <td colSpan={isAdmin ? 7 : 6} className="px-5 py-14 text-center">
                                        <FileSignature size={36} className="mx-auto text-[color:var(--bm-text-subtle)]" />
                                        <div className="mt-3 text-[color:var(--bm-text-muted)] text-[14px]">No contracts yet.</div>
                                        <div className="text-[12.5px] text-[color:var(--bm-text-subtle)] mt-1">Contracts are created automatically when a client accepts a proposal.</div>
                                    </td>
                                </tr>
                            )}
                            {sortedItems.map(c => {
                                const days = daysFromToday(c.renewal_date);
                                const soon = days != null && days <= 30 && days >= 0;
                                const isSel = selected.has(c.id);
                                return (
                                    <tr key={c.id} onClick={() => navigate(`/contracts/${c.id}`)}
                                        className={`border-t border-[color:var(--bm-border)] hover:bg-[color:var(--bm-bg-mute)] cursor-pointer ${isSel ? 'bg-[color:var(--bm-accent-50)]/60' : ''}`}
                                        data-testid={`contract-row-${c.id}`}>
                                        {isAdmin && (
                                            <td className="px-4 py-3.5" onClick={(e) => { e.stopPropagation(); toggleOne(c.id); }}>
                                                <input type="checkbox" checked={isSel} onChange={() => toggleOne(c.id)}
                                                       onClick={(e) => e.stopPropagation()} className="cursor-pointer"
                                                       data-testid={`contract-select-${c.id}`} />
                                            </td>
                                        )}
                                        <td className="px-5 py-3.5">
                                            <div className="font-medium text-[color:var(--bm-text)]">{c.contract_number}</div>
                                            <div className="text-[11.5px] text-[color:var(--bm-text-subtle)]">from {c.proposal_number}</div>
                                        </td>
                                        <td className="px-4 py-3.5 text-[color:var(--bm-text-muted)]">{c.client_name}</td>
                                        <td className="px-4 py-3.5">
                                            <span className={`bm-pill ${CONTRACT_STATUS[c.status]?.tone || ''} border`}>
                                                {CONTRACT_STATUS[c.status]?.label || c.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3.5 text-right font-medium">{GBP(c.monthly_saas_fee)}</td>
                                        <td className="px-4 py-3.5 text-right font-medium">{GBP(c.total_inc_vat_year_one)}</td>
                                        <td className="px-4 py-3.5">
                                            <div className="flex items-center gap-1.5">
                                                <CalendarClock size={14} className={soon ? 'text-amber-600' : 'text-[color:var(--bm-text-subtle)]'} />
                                                <span className={soon ? 'text-amber-700 font-medium' : 'text-[color:var(--bm-text-muted)]'}>
                                                    {c.renewal_date} {days != null && days >= 0 && <span className="text-[11.5px]">({days}d)</span>}
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <BulkDeleteDialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} info={confirmDelete}
                              entityLabel="contract" onConfirm={doDelete} testId="contracts-bulk-delete-dialog" />
        </div>
    );
}
